import { useCallback, useEffect, useMemo, useState } from "react";

import type { TimelineEvent } from "~/lib/matrix";

export type EditRecallOrigin = "manual" | "recall";
export type EditRecallOlderResult = "moved" | "at-oldest" | "empty";

export interface UseEditRecallResult {
  current: string | null;
  origin: EditRecallOrigin | null;
  open: (eventId: string, origin: EditRecallOrigin) => void;
  recallOlder: () => EditRecallOlderResult;
  recallNewer: () => boolean;
  exit: () => void;
}

function isEditableOwnTextEvent(event: TimelineEvent, userId: string): boolean {
  return (
    event.sender.id === userId &&
    event.content.type === "m.text" &&
    event.status === "sent" &&
    event.encryptionStatus !== "undecryptable" &&
    !event.redacted
  );
}

export function useEditRecall(
  events: readonly TimelineEvent[],
  userId: string | null,
): UseEditRecallResult {
  const orderedIds = useMemo(() => {
    if (!userId) return [];
    return events.filter((event) => isEditableOwnTextEvent(event, userId)).map((event) => event.id);
  }, [events, userId]);

  const [current, setCurrent] = useState<string | null>(null);
  const [origin, setOrigin] = useState<EditRecallOrigin | null>(null);

  useEffect(() => {
    if (current === null) return;
    if (orderedIds.includes(current)) return;
    setCurrent(null);
    setOrigin(null);
  }, [current, orderedIds]);

  const open = useCallback((eventId: string, nextOrigin: EditRecallOrigin) => {
    setCurrent(eventId);
    setOrigin(nextOrigin);
  }, []);

  const recallOlder = useCallback(() => {
    if (orderedIds.length === 0) return "empty";
    setOrigin("recall");
    if (current === null) {
      setCurrent(orderedIds[orderedIds.length - 1] ?? null);
      return "moved";
    }
    const idx = orderedIds.indexOf(current);
    if (idx === -1) {
      setCurrent(orderedIds[orderedIds.length - 1] ?? null);
      return "moved";
    }
    if (idx === 0) return "at-oldest";
    setCurrent(orderedIds[idx - 1] ?? current);
    return "moved";
  }, [current, orderedIds]);

  const recallNewer = useCallback(() => {
    if (current === null) return false;
    const idx = orderedIds.indexOf(current);
    if (idx === -1 || idx >= orderedIds.length - 1) {
      setCurrent(null);
      setOrigin(null);
      return true;
    }
    setCurrent(orderedIds[idx + 1] ?? null);
    setOrigin("recall");
    return false;
  }, [current, orderedIds]);

  const exit = useCallback(() => {
    setCurrent(null);
    setOrigin(null);
  }, []);

  return { current, origin, open, recallOlder, recallNewer, exit };
}
